import { makeAutoObservable, runInAction } from 'mobx'
import { ddayjs, trace } from '@dt/fe-utils'
import { productMobx, menuMobx, restaurantConfig, restaurantCenter, RESTAURANT_EVENT, cartMobx } from '../..'
import { mapValues, padStart } from '@dt/fe-utils/dist/lodash'
import ProductAPI from 'api/product.api'

interface IProductMenu {
  begin: string; // HHmmss
  end: string; // HHmmss
  product: any;
  category: any;
  /** 是否未加载 */
  noLoad?: boolean;
}

export function convertHmsToHm (hmstr: string) {
  const hms = hmstr.split('')
  return hms.length === 5 ? `${hms[0]}:${hms[1]}${hms[2]}` : `${hms[0]}${hms[1]}:${hms[2]}${hms[3]}`
}

function convertHmsToStr (hmsint: number) {
  const hms = padStart((hmsint + ''), 5, '0')
  return hms.length === 5 ? '0' + hms : hms
}

class ProductCenter {
  // 暂留品牌ID 和 门店ID
  private brandId: number = 0
  storeId: number = 0
  constructor () {
    makeAutoObservable(this)
    restaurantCenter.on(RESTAURANT_EVENT.RESTAURANT_INIT, () => {
      this.init(restaurantConfig.brandId, restaurantConfig.storeId)
    })
    restaurantCenter.on(RESTAURANT_EVENT.PRODUCT_CHANGED, (data: any) => {
      // POS渠道餐品初始化忽略三方外卖 ｜ 团餐渠道菜单
      if (!data || data.channel !== '3rdDelivery' || data.channel !== 'groupMeal') {
        this.initProduct()
      }
    })
    restaurantCenter.on(RESTAURANT_EVENT.MENU_REFRESH, () => {
      // 如果是手机因为菜单没有完全获取所以需要往前跳转，POS则只需要重新应用apply
      if (restaurantConfig.app === 'mobile') {
        this.forward()
      } else {
        this.apply()
      }
    })
  }

  /** 当前门店所有的菜单 */
  menus: IProductMenu[] = []

  /** 基准的日期,采用 YYYY-MM-DD的格式 */
  appointmentDate?: string

  /** 基准的时间,采用 HH:mm的格式 */
  appointmentTime?: string

  /** 是否初始化 */
  isInited: boolean = false

  // 餐品中心的init 承接该门店所有的菜单
  async initProduct (eatType?: string, storeId?: number) {
    menuMobx.setIsLoading(true)
    if (restaurantConfig.app === 'mobile') {
      productMobx.reset() // 初始化前重置餐品数据
    }
    if (!this.storeId && !storeId) {
      return
    }
    const _eatType = eatType || cartMobx.info.eatType
    // 只有品牌码是用外卖菜单(堂食)
    const _channel = restaurantConfig.app === 'mobile' ? (restaurantConfig.codeType === 'brand' && _eatType !== 'dinein' ? 'pickup' : 'mobile') : restaurantConfig.app === 'kiosk' ? 'kiosk' : 'pos'
    if (restaurantConfig.app === 'mobile' && !_eatType) {
      return
    }
    restaurantCenter.emit(RESTAURANT_EVENT.MENU_START)
    const option: any = restaurantConfig.app === 'mobile' ? {
      avatar: 1,
      sale: 1,
      menuBeginTime: this.appointmentTime || '',
      menuBeginDate: this.appointmentDate || '',
      channel: _channel,
      eatType: _eatType
    } : (this.appointmentTime || '')
    return ProductAPI.getProduct(this.brandId, (this.storeId || storeId) as number, option).then((menus: any) => {
      runInAction(() => {
        this.menus = menus
        this.isInited = true
      })
      // restaurantCenter.emit(RESTAURANT_EVENT.MENU_APPLY)
      // TODO 当前如果在外卖 | 团餐的菜单的时候，则不自动应用菜单
      if (productMobx.channel !== '3rdDelivery' && productMobx.channel !== 'groupMeal') {
        this.apply()
      }
      console.log('菜单接口返回getProduct =====> ', menus)
      menuMobx.setIsLoading(false)
      return menus
    }, (err) => {
      console.log('菜单加载异常', err)
      trace.traceLog({
        name: '菜单: => 加载异常',
        message: { ...option, brandId: restaurantConfig.brandId, storeId: restaurantConfig.storeId }
      })
      // 请求被关闭可以不当做异常
      if (!(err.message === 'canceled')) {
        menuMobx.setIsError(true)
      }
      restaurantCenter.emit(RESTAURANT_EVENT.MENU_END)
    })
  }

  /** 计算是否全天菜单 */
  get isAllDayMenu (): boolean {
    return this.menus.length === 1 && this.menus[0].begin === '000000' && this.menus[0].end === '240000'
  }

  /** 获取当前 设备的全量菜单id */
  get getPosMenu () {
    return mapValues(this.menus.reduce<{category?: string[]; products?: string[]}>((t, c) => ({
      ...t,
      category: (t?.category || []).concat(c.category.map((item: { no: string }) => item.no)),
      products: (t?.products || []).concat(c.product.map((item: { no: string }) => item.no))
    }), {}), (i) => new Set(i))
  }

  /** 设置storeId，需要在切换就餐方式时传入门店数据 */
  setStore (brandId: number = this.brandId, storeId: number = this.storeId) {
    this.brandId = brandId
    this.storeId = storeId
  }

  /**
   * 处理时间说明，菜单的时段使用字符串 "HH:mm", 计算使用int型的 HHmmss，需要特殊对待0:00
   * menu noLoad专门针对morder后端未返回该本菜单数据的标记位
   */
  apply (_baseTime?: string) {
    // 应用菜单的基准时间
    const baseTime = _baseTime || (this.appointmentTime ? this.appointmentTime.replace(':', '') + '00' : null) || ddayjs().format('HHmmss')
    // 当前的时间
    // const presentTime = _baseTime ? ddayjs().format('HHmmss') : baseTime
    // 选择菜单
    const theMenu = this.menus.find((menu: IProductMenu) => parseInt(menu.begin) <= parseInt(baseTime) && parseInt(menu.end) > parseInt(baseTime) && !menu.noLoad)
    restaurantCenter.emit(RESTAURANT_EVENT.MENU_APPLY)
    // console.log('🍒 当前门店的所有菜单', this.menus)
    if (!this.menus.length) {
      // console.log('🍒 无菜单')
      productMobx.reset()
    } else if (theMenu && this.menus[0].begin === '000000' && this.menus[0].end === '240000') { // 一本菜单的处理方案，全天则不再有切换任务，非全天依然会切换
      productMobx.init(theMenu)
      menuMobx.setRange({ begin: theMenu.begin, end: theMenu.end })
      // console.log('🍒 当前选择的菜单', theMenu)
      // console.log('🍒 当前门店的时间点', ddayjs().format())
    } else if (theMenu) { // 当前的基准时间有菜单,切非一本全天
      const theMenuEndValue = ddayjs(theMenu.end, 'HHmmss').valueOf()
      // console.log('🍒 当前选择的菜单', theMenu)
      // console.log('🍒 当前门店的时间点', ddayjs().format())
      if (!this.appointmentTime) {
        menuMobx.setNextRefreshTime(theMenuEndValue)
      }
      // console.log('🍒 下一个刷新时间点', ddayjs(theMenu.end, 'HHmmss').format())
      productMobx.init(theMenu)
      menuMobx.setRange({ begin: theMenu.begin, end: theMenu.end })
    } else {
      // 将获取下一个节点的菜单渲染，但是这本菜单当前并不可用 （pos和手机点餐通用做法）, 如果找不到则是第一本（第二天）
      // console.log('🍒 基准的时间', baseTime)
      let theFutureMenu: IProductMenu = this.menus.find(menu => parseInt(menu.begin) > parseInt(baseTime)) || this.menus[0]
      // console.log('🍒 当前选择的菜单', theFutureMenu)
      // console.log('🍒 当前门店的时间点', ddayjs().format())
      let theNextTime = ddayjs(theFutureMenu.begin, 'Hmmss').valueOf() - ddayjs(baseTime, 'Hmmss').valueOf()
      if (theNextTime > 0) {
        theNextTime = ddayjs(theFutureMenu.end, 'HHmmss').valueOf() // 24 * 60 * 60 * 1000 - theNextTime
      } else {
        theNextTime = ddayjs(theFutureMenu.end, 'HHmmss').valueOf() + 24 * 60 * 60 * 1000
      }
      if (!this.appointmentTime) {
        menuMobx.setNextRefreshTime(theNextTime)
      }
      // console.log('🍒 下一个刷新时间点', ddayjs(theNextTime).format())
      productMobx.init(theFutureMenu)
      menuMobx.setRange({ begin: theFutureMenu.begin, end: theFutureMenu.end })
    }
    restaurantCenter.emit(RESTAURANT_EVENT.MENU_END)
  }

  async init (brandId: number = this.brandId, storeId: number = this.storeId) {
    this.brandId = brandId
    this.storeId = storeId
    // 初始化 获取餐品 菜单排序 餐品售卖状态
    const products = await this.initProduct()
    runInAction(() => {
      this.isInited = true
    })
    return products
    // await this.initProductSale()
  }

  /** 获取下一个菜单(可能是提前的) */
  async forward (eatType?: string) {
    menuMobx.setIsLoading(true)
    if (restaurantConfig.app === 'mobile') {
      productMobx.reset() // 初始化前重置餐品数据
    }
    restaurantCenter.emit(RESTAURANT_EVENT.MENU_START)
    // 特殊处理24:00 这样的case，认为24:00 则为00:00
    const theEnd = menuMobx.range.end === '240000' ? '000000' : menuMobx.range.end
    const _eatType = eatType || cartMobx.info.eatType
    const _channel = restaurantConfig.app === 'mobile' ? (restaurantConfig.codeType === 'brand' && _eatType !== 'dinein' ? 'pickup' : 'mobile') : 'pos'
    if (restaurantConfig.app === 'mobile') {
      // TODO 获取失败的错误处理
      // 将当前菜单的结束时间转换成 string HH:mm
      const option: any = restaurantConfig.app === 'mobile' ? {
        menuBeginTime: convertHmsToHm(theEnd),
        channel: _channel,
        eatType: _eatType
      } : convertHmsToHm(theEnd)
      const menus = await ProductAPI.getProduct(this.brandId, this.storeId, option)
      runInAction(() => {
        this.menus = menus
      })
    }
    // restaurantCenter.emit(RESTAURANT_EVENT.MENU_APPLY)
    // 加1s 往前跃进 *确保保持Hms的标准字符串
    const forwardTime = parseInt(theEnd) + 1
    this.apply(convertHmsToStr(forwardTime))
    menuMobx.setIsLoading(false)
  }
  getDetail (no: string) {
    let detail = {}
    this.menus[0].product.forEach((item: any) => {
      if (item.no === no) {
        detail = item
      }
    })
    return detail
  }
  // appointmentTime HH:mm
  setAppointmentTime (appointmentTime?: string, cb?: (isInit: boolean) => void) {
    let isInit = false
    if (!appointmentTime) {
      this.appointmentTime = appointmentTime
    } else if (appointmentTime && this.appointmentTime !== appointmentTime) {
      this.appointmentTime = appointmentTime
      const atime = parseInt(appointmentTime.replace(':', '')) * 100
      // 判断当前可用菜单是否在范围内
      if (atime < parseInt(menuMobx.range.begin) || atime >= parseInt(menuMobx.range.end)) {
        this.init()
        isInit = true
      }
    }
    cb && cb(isInit)
  }

  // appointmentDate YYYY-MM-DD
  setAppointmentDate (appointmentDate?: string, cb?: () => void) {
    if (this.appointmentDate !== appointmentDate) {
      this.appointmentDate = appointmentDate
    }
    cb && cb()
  }

  /**
   * 获取下一本菜单的信息
   */
  getNextMenu () {
    const baseTime = (this.appointmentTime ? this.appointmentTime.replace(':', '') + '00' : null) || ddayjs().format('HHmmss')
    let theNextMenu: IProductMenu = this.menus.find(menu => parseInt(menu.begin) > parseInt(baseTime)) || this.menus[0]
    return {
      theNextMenu,
      begin: convertHmsToHm(theNextMenu.begin)
    }
  }
}

export default new ProductCenter()
